// src/features/webhooks/EventTypeChips.tsx
// Event-type multiselect: "subscribe to all" toggle + wrapped chip list.
// Shared by the create dialog and the detail page's inline event-types editor.
// See docs/ux/14-forms.md.

import { useI18n } from '@react/hooks';

export interface EventTypeChipsProps {
  availableTypes: string[];
  selected: string[];
  subscribeAll: boolean;
  onToggle: (eventType: string) => void;
  onSubscribeAllChange: (subscribeAll: boolean) => void;
  disabled?: boolean;
}

export function EventTypeChips({
  availableTypes,
  selected,
  subscribeAll,
  onToggle,
  onSubscribeAllChange,
  disabled,
}: EventTypeChipsProps) {
  const { t } = useI18n();

  return (
    <div className="space-y-2">
      <label
        className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        <input
          type="checkbox"
          checked={subscribeAll}
          disabled={disabled}
          onChange={(e) => onSubscribeAllChange(e.target.checked)}
          className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
        />
        <span className="text-sm font-medium text-foreground">{t('webhooks.subscribeAll')}</span>
      </label>

      {!subscribeAll &&
        (availableTypes.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {availableTypes.map((et) => (
              <button
                key={et}
                type="button"
                disabled={disabled}
                onClick={() => onToggle(et)}
                aria-pressed={selected.includes(et)}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  selected.includes(et)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-foreground/20'
                } ${disabled ? 'pointer-events-none opacity-50' : ''}`}
              >
                {et}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('webhooks.loadingEventTypes')}</p>
        ))}
    </div>
  );
}

export default EventTypeChips;
